import { forwardRef, useImperativeHandle, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { api } from '../lib/api.js';
import { WEB_URL } from '../lib/platform.js';
import { siteStripe, stripeAppearance } from '../lib/stripe.js';
import { Spinner } from './ui.js';
import { payBase, payQuery, PayError, type PayablePending, type PayRes } from './Pay.js';

/*
 * Paying as a guest (docs/30): no account, so no platform card to clone. The Payment Element sits on the site's own Stripe
 * instance in deferred mode (amount known, intent not yet created); on Pay the order is created, the server starts one
 * PaymentIntent on the site's account (the same path as Apple Pay), the phone confirms it with the card typed here, and the
 * server confirms.
 * `allowedPaymentMethodTypes: ['card']` is deliberate: deferred mode otherwise offers every method switched on in the Stripe
 * Dashboard (Pay by Bank, Klarna, Revolut Pay, Amazon Pay, Link; Saif, 3 Nov 2026), and the server creates the intent as card
 * only, so anything else would fail at confirm. Apple Pay / Google Pay for a guest use the usual Express Checkout button with the guest's own routes.
 */

export interface GuestCardHandle {
  /** Validates the card form (Stripe's own checks). */
  submit(): Promise<void>;
  /** Confirms the server's intent with the card typed here; 3-D Secure opens in place or returns to `returnUrl`. */
  confirm(clientSecret: string, amountPence: number, returnUrl: string): Promise<void>;
}

interface Props {
  pk: string;
  accountId: string;
  amountPence: number;
  onReady?: () => void;
}

export const GuestCardForm = forwardRef<GuestCardHandle, Props>(function GuestCardForm({ pk, accountId, amountPence, onReady }, ref) {
  const [stripe] = useState<Promise<Stripe | null>>(() => siteStripe(pk, accountId));
  if (amountPence <= 0) return null;
  return (
    <div className="newcard">
      <Elements stripe={stripe} options={{ mode: 'payment', amount: amountPence, currency: 'gbp', allowedPaymentMethodTypes: ['card'], appearance: stripeAppearance() }}>
        <GuestCardInner ref={ref} onReady={onReady} />
      </Elements>
    </div>
  );
});

const GuestCardInner = forwardRef<GuestCardHandle, { onReady?: (() => void) | undefined }>(function GuestCardInner({ onReady }, ref) {
  const stripe = useStripe();
  const elements = useElements();
  const [ready, setReady] = useState(false);
  useImperativeHandle(ref, () => ({
    async submit() {
      if (!stripe || !elements) throw new PayError('The card form is still loading');
      const { error } = await elements.submit();
      if (error) throw new PayError(error.message ?? 'Check your card details');
    },
    async confirm(clientSecret, amountPence, returnUrl) {
      if (!stripe || !elements) throw new PayError('The card form is still loading');
      elements.update({ amount: amountPence });
      const { error } = await stripe.confirmPayment({ elements, clientSecret, confirmParams: { return_url: returnUrl }, redirect: 'if_required' });
      if (error) throw new PayError(error.message ?? 'Your card was declined. Try another card.');
    },
  }), [stripe, elements]);
  return (
    <div className="col" style={{ gap: 10 }}>
      {!ready && <div className="center" style={{ padding: 16 }}><Spinner /></div>}
      <div className="stripe-box" style={ready ? undefined : { height: 0, overflow: 'hidden' }}><PaymentElement onReady={() => { setReady(true); onReady?.(); }} options={{ layout: 'tabs', terms: { card: 'never' }, wallets: { applePay: 'never', googlePay: 'never' } }} /></div>
    </div>
  );
});

/** The guest's card payment, start to finish: validate, one intent on the site's account, confirm on the phone, confirm on the server. */
export async function payGuestCard(form: GuestCardHandle, p: PayablePending): Promise<string> {
  await form.submit();
  const r = await api<PayRes>(`${payBase(p)}/pay${payQuery(p)}`, { body: { method: 'wallet' } });
  if (r.status === 'succeeded') return r.payLabel ?? 'card';
  if (r.status === 'cancelled') throw new PayError(r.message ?? 'This order was cancelled.');
  if (r.status !== 'wallet' || !r.clientSecret) throw new PayError(r.message ?? 'Could not start the payment');
  await form.confirm(r.clientSecret, p.cardPence, `${WEB_URL}/pay/return?kind=${p.kind}&id=${p.id}${p.returnExtra ?? ''}`);
  const c = await api<PayRes>(`${payBase(p)}/confirm${payQuery(p)}`, { body: {} });
  if (c.status === 'succeeded') return c.payLabel ?? 'card';
  throw new PayError(c.message ?? 'The payment did not go through.');
}
